import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, ChevronDown, ChevronUp, Plus, ArrowDownUp } from "lucide-react";
import { toast } from "react-toastify";

import "../../style/employeeManagement.css";
import TotalAdminIcon from "../../assets/total-admin-icon.svg";
import TotalUsersIcon from "../../assets/total-users-icon.svg";
import AddAndEditEmpMan from "./AddAndEditEmpMan";
import {
  getAdminCount,
  getEmployeeList,
  deleteEmployee,
  updateEmployeeStatus,
} from "../../services/employe-management-api";

import FilterIcon from "../../assets/icons/FilterIcon";
import SearchIcon from "../../assets/search-icon.svg";
import EditIcon from "../../assets/icons/EditIcon";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import { useSelector } from "react-redux";

function EmployeeManagement() {
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showRoleFilterDropdown, setShowRoleFilterDropdown] = useState(false);
  const [showActiveFilterDropdown, setShowActiveFilterDropdown] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalAdmins: 0,
    activeAdmins: 0,
    activeEmployee: 0,
  });

  const roleFilterDropdownRef = useRef(null);
  const activeFilterDropdownRef = useRef(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await getAdminCount();
      const data = response.data || response;
      setStats({
        totalAdmins: data.total_admins || 0,
        activeAdmins: data.active_admins || 0,
        activeEmployee: data.active_employee || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to fetch statistics");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(activeFilter !== "all" && { status: activeFilter }),
        ...(roleFilter !== "all" && { role: roleFilter }),
      };
      const data = await getEmployeeList(params);
      const employees = (data.results || []).map((emp) => ({
        id: emp.id,
        name: emp.name || "-",
        email: emp.display_email,
        role: emp.admin_role,
        status: emp.status ? "Active" : "Inactive",
        accountAccess: emp.status,
      }));
      setFilteredOrders(employees);
      setTotalCount(data.count || 0);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch employees");
      setFilteredOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage, itemsPerPage, roleFilter, activeFilter]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (sortField && sortDirection) {
      const sorted = [...filteredOrders].sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];

        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        const comparison = aValue > bValue ? 1 : -1;
        return sortDirection === "asc" ? comparison : -comparison;
      });
      setFilteredOrders(sorted);
    } else {
      fetchOrders();
    }
  }, [
    searchTerm,
    currentPage,
    roleFilter,
    activeFilter,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        roleFilterDropdownRef.current &&
        !roleFilterDropdownRef.current.contains(event.target)
      ) {
        setShowRoleFilterDropdown(false);
      }
      if (
        activeFilterDropdownRef.current &&
        !activeFilterDropdownRef.current.contains(event.target)
      ) {
        setShowActiveFilterDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField("");
        setSortDirection("");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const currentOrders = filteredOrders;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const shouldShowPagination = totalCount > itemsPerPage && totalPages > 1;

  const SortableHeader = ({ field, children, showIcon = false }) => {
    const isActive = sortField === field;
    return (
      <th className="emp-sortable-header" onClick={() => handleSort(field)}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {children}
          {showIcon && (
            <ArrowDownUp
              size={16}
              style={{ color: isActive ? "#2F80ED" : "#858D9D" }}
            />
          )}
        </div>
      </th>
    );
  };

  const getStatusBadge = (status) => {
    if (!status) return null;

    const statusLower = status.toLowerCase();
    let className = "emp-status-badge";

    if (statusLower === "active") {
      className += " emp-status-active";
    } else if (statusLower === "inactive") {
      className += " emp-status-inactive";
    }

    return <span className={className}>{status}</span>;
  };

  const getRoleBadge = (role) => {
    if (!role) return null;

    const roleLower = role.toLowerCase();
    let className = "emp-role-badge";

    if (roleLower === "admin") {
      className += " emp-role-admin";
    } else if (roleLower === "employee") {
      className += " emp-role-employee";
    }

    return <span className={className}>{role}</span>;
  };

  const handleToggleAccess = async (employeeId, currentAccess) => {
    try {
      await updateEmployeeStatus(employeeId, !currentAccess);
      toast.success("Account access updated successfully");
      fetchOrders();
    } catch (error) {
      console.error("Error updating access:", error);
      toast.error("Failed to update account access");
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) {
      return;
    }
    try {
      await deleteEmployee(employeeId);
      toast.success("Employee deleted successfully");
      fetchOrders();
      fetchStats();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Failed to delete employee");
    }
  };

  const roleFilterOptions = [
    { value: "all", label: "All Role" },
    { value: "ADMIN", label: "Admin" },
    { value: "EMPLOYEE", label: "Employee" },
  ];

  const activeFilterOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const getRoleFilterLabel = () => {
    const option = roleFilterOptions.find((opt) => opt.value === roleFilter);
    return option ? option.label : "All Role";
  };

  const handleRoleFilterSelect = (value) => {
    setRoleFilter(value);
    setShowRoleFilterDropdown(false);
    setCurrentPage(1);
  };

  const getActiveFilterLabel = () => {
    const option = activeFilterOptions.find(
      (opt) => opt.value === activeFilter
    );
    return option ? option.label : "All Status";
  };

  const handleActiveFilterSelect = (value) => {
    setActiveFilter(value);
    setShowActiveFilterDropdown(false);
    setCurrentPage(1);
  };

  return (
    <>
      <div className="card rounded-3 p-4 border-0 mb-1 emp-container">
        <div className="emp-layout">
          <div className="emp-top">
            <div className="emp-header">
              <div>
                <h2 className="emp-title">Employee Management</h2>
                <p className="emp-subtitle">
                  Manage all your employees, update their access, and control
                  account activity from one place.
                </p>
              </div>

              <div className="d-flex gap-2 align-items-center">
                {/* Add Employee Button */}
                <button
                  className={` ${
                    isPartsURL
                      ? "add-customer-btn-parts"
                      : isAccessoriesURL
                      ? "add-customer-btn-acc"
                      : isGodownURL
                      ? "add-customer-btn-godown"
                      : "add-customer-btn"
                  }`}
                  onClick={() => {
                    setSelectedEmployee(null);
                    setShowModal(true);
                  }}
                >
                  <Plus size={16} /> Add Employee
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="row mb-2">
              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Users
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          stats.totalAdmins
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalUsersIcon} alt="Total Admin" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Admin
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          stats.activeAdmins
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalAdminIcon} alt="Total Users" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Employee
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          stats.activeEmployee
                        )}
                      </h4>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalAdminIcon} alt="Total Employee" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="emp-search-filter-section mb-4">
              {/* Search Container */}
              <div className="emp-search-container">
                <input
                  type="text"
                  className={`${
                    isPartsURL
                      ? "emp-search-input-parts"
                      : isAccessoriesURL
                      ? "emp-search-input-acc"
                      : "emp-search-input"
                  }`}
                  placeholder="Search by name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="emp-search-icon"
                />
              </div>

              {/* Role Filter Dropdown */}
              <div className="emp-filter-dropdown" ref={roleFilterDropdownRef}>
                <button
                  className={`${
                    isPartsURL
                      ? "emp-statuses-filter-btn-parts"
                      : isAccessoriesURL
                      ? "emp-statuses-filter-btn-acc"
                      : "emp-statuses-filter-btn"
                  }`}
                  onClick={() =>
                    setShowRoleFilterDropdown(!showRoleFilterDropdown)
                  }
                >
                  <FilterIcon size={16} />
                  {getRoleFilterLabel()}
                  {showRoleFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="emp-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="emp-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`emp-filter-dropdown-content ${
                    showRoleFilterDropdown ? "show" : ""
                  }`}
                >
                  {roleFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`emp-filter-dropdown-item ${
                        roleFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleRoleFilterSelect(option.value)}
                    >
                      <Check size={16} className="emp-filter-dropdown-check" />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Filter Dropdown */}
              <div
                className="emp-filter-dropdown"
                ref={activeFilterDropdownRef}
              >
                <button
                  className={`${
                    isPartsURL
                      ? "emp-statuses-filter-btn-parts"
                      : isAccessoriesURL
                      ? "emp-statuses-filter-btn-acc"
                      : "emp-statuses-filter-btn"
                  }`}
                  onClick={() =>
                    setShowActiveFilterDropdown(!showActiveFilterDropdown)
                  }
                >
                  <FilterIcon size={16} />
                  {getActiveFilterLabel()}
                  {showActiveFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="emp-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="emp-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`emp-filter-dropdown-content ${
                    showActiveFilterDropdown ? "show" : ""
                  }`}
                >
                  {activeFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`emp-filter-dropdown-item ${
                        activeFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleActiveFilterSelect(option.value)}
                    >
                      <Check size={16} className="emp-filter-dropdown-check" />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="emp-table-wrapper">
            <div className="emp-table-responsive">
              {/* Employee Table */}
              <table className="emp-table">
                <thead>
                  <tr>
                    <th>EMPLOYEE NAME</th>
                    <SortableHeader field="email" showIcon={true}>
                      EMAIL ADDRESS
                    </SortableHeader>
                    <th>ROLE</th>
                    <th>STATUS</th>
                    <th>ACCOUNT ACCESS</th>

                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading employees...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : currentOrders.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>
                            No employees found
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentOrders.map((employee) => {
                      const isAdmin = employee.role?.toLowerCase() === "admin";
                      return (
                        <tr key={employee.id}>
                          <td>
                            <div className="emp-tooltip">
                              <span className="emp-truncate-text">
                                {employee?.name || "-"}
                              </span>
                              <span className="emp-tooltiptext">
                                {employee?.name || "-"}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="emp-tooltip">
                              <span className="emp-truncate-text">
                                {employee?.email || "-"}
                              </span>
                              <span className="emp-tooltiptext">
                                {employee?.email || "-"}
                              </span>
                            </div>
                          </td>
                          <td>{getRoleBadge(employee.role)}</td>
                          <td>{getStatusBadge(employee.status)}</td>
                          <td>
                            <label className={`${isPartsURL ? "emp-toggle-switch-parts" : isAccessoriesURL ? "emp-toggle-switch-acc" : "emp-toggle-switch"}`}>
                              <input
                                type="checkbox"
                                checked={employee.accountAccess}
                                onChange={() =>
                                  handleToggleAccess(
                                    employee.id,
                                    employee.accountAccess
                                  )
                                }
                                disabled={isAdmin}
                              />
                              <span className="emp-toggle-slider"></span>
                            </label>
                          </td>
                          <td>
                            <div className="action-buttons">
                              {/* Edit Button */}
                              <button
                                className={`${isPartsURL ? "action-btn-parts view" : isAccessoriesURL ? "action-btn-acc view" : "action-btn view"}`}
                                title="Edit employee"
                                onClick={() => {
                                  setSelectedEmployee(employee);
                                  setShowModal(true);
                                }}
                              >
                                <EditIcon fill={isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)": isGodownURL ? "#3b82f6" : "var(--primary-color-mobile)"} />
                              </button>

                              {/* Delete Button */}
                              <button
                                className="action-btn delete"
                                title="Delete employee"
                                onClick={() =>
                                  handleDeleteEmployee(employee.id)
                                }
                              >
                                <RedDeleteIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {shouldShowPagination && (
            <div className="emp-pagination-sticky">
              <div className="emp-pagination-container">
                <div className="emp-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="emp-pagination-buttons">
                  <button
                    className={`${isPartsURL ? "emp-pagination-btn-parts" : isAccessoriesURL ? "emp-pagination-btn-acc" : "emp-pagination-btn"}`}
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`${isPartsURL ? "emp-next-pagination-btn-parts" : isAccessoriesURL ? "emp-next-pagination-btn-acc" : "emp-next-pagination-btn"}`}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddAndEditEmpMan
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        onSave={() => {
          setShowModal(false);
          setSelectedEmployee(null);
          fetchOrders();
          fetchStats();
        }}
      />
    </>
  );
}

export default EmployeeManagement;
